import {
  cloneDeep,
  difference,
  filter,
  forEach,
  fromPairs,
  includes,
  isArray,
  isEmpty,
  isString,
  join,
  keys,
  map,
  pull,
  pullAt,
  reduce,
  some,
  toPairs,
  uniq,
} from 'lodash';

import { ResultType } from 'interfaces/experimentResults';
import GridBasedCalculationParams from 'interfaces/girdBasedCalculationParams';
import { JobPreset } from 'interfaces/jobPreset';
import { PostProcessingActionCreated } from 'interfaces/postProcessingAction';
import { VisualizationCreated } from 'interfaces/visualization';

export enum Action {
  KEEP = 'Keep',
  DELETE = 'Delete',
  REASSIGN = 'Reassign',
}

export interface StainsActions {
  [stain: string]: { action: Action; reassignTo?: string };
}

export interface PresetApplyParams {
  name: string;
  postprocessing: PostProcessingActionCreated[];
  features: PostProcessingActionCreated[];
  visualizations: VisualizationCreated[];
  gridBasedCalculationParams: Record<string, GridBasedCalculationParams>;
}

const quantificationResultTypes = [ResultType.CellQuantification, ResultType.AreaQuantification];
const quantificationGeometries = ['nuclear', 'membrane', 'cytoplasm'];
const quantificationMeasurements = ['mean_intensity', 'od'];

// Escapes regex special characters in dynamic stain IDs (e.g. "he+if") so they are matched literally.
const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const quantificationResultTypesPattern = join(quantificationResultTypes, '|');
const quantificationGeometriesPattern = join(quantificationGeometries, '|');
const quantificationMeasurementsPattern = join(quantificationMeasurements, '|');

// Remaps quantification identifiers that encode a stain either as:
//   oldStain_cell_quantification....                -> newStain_cell_quantification....
//   cell_quantification.marker.oldStain....        -> cell_quantification.marker.newStain....
//   oldStain_cell_quantification.marker.oldStain.. -> newStain_cell_quantification.marker.newStain..
//   marker.oldStain.geometry....quant....(.binned) -> marker.newStain.geometry....quant....(.binned)
// while leaving non-quantification identifiers unchanged.
const remapQuantificationIdentifier = (value: string, oldStainToNewStain: Record<string, string>): string => {
  let remappedValue = value;

  forEach(oldStainToNewStain, (newStain, oldStain) => {
    // Skip invalid/no-op remaps.
    if (!newStain || oldStain === newStain) {
      return;
    }

    // Regex breakdown:
    // ^                         start of identifier
    // ${escapeRegExp(oldStain)} literal old stain id (escaped)
    // _                         separator between stain and result type
    // (cell_quantification|area_quantification)
    //                           capture group #1: supported quantification result type
    // (\.|$)                   capture group #2: either next dot segment or end of string
    //
    // This intentionally remaps ONLY quantification-prefixed identifiers.
    const quantificationPrefixPattern = new RegExp(
      `^${escapeRegExp(oldStain)}_(${quantificationResultTypesPattern})(\\.|$)`
    );

    const quantificationMarkerPattern = new RegExp(
      `(^|[._])(${quantificationResultTypesPattern})\\.marker\\.${escapeRegExp(oldStain)}(?=\\.|$)`,
      'g'
    );

    const rawQuantificationMarkerPattern = new RegExp(
      `(^|[._])marker\\.${escapeRegExp(
        oldStain
      )}\\.geometry\\.(${quantificationGeometriesPattern})\\.quant\\.(${quantificationMeasurementsPattern})(?=\\.|$)`,
      'g'
    );

    // Rebuild with new stain, while preserving captured result type ($1) and suffix boundary ($2).
    remappedValue = remappedValue.replace(quantificationPrefixPattern, `${newStain}_$1$2`);
    remappedValue = remappedValue.replace(quantificationMarkerPattern, `$1$2.marker.${newStain}`);
    remappedValue = remappedValue.replace(rawQuantificationMarkerPattern, `$1marker.${newStain}.geometry.$2.quant.$3`);
  });

  return remappedValue;
};

// Deeply remaps quantification identifiers in the full preset payload:
// - string values (e.g. action params)
// - object keys (e.g. backend logical-query mappingFilter keys)
// - arrays and nested objects at any depth.
const remapQuantificationIdentifiersRecursively = (
  value: unknown,
  oldStainToNewStain: Record<string, string>
): unknown => {
  if (isString(value)) {
    return remapQuantificationIdentifier(value, oldStainToNewStain);
  }

  if (isArray(value)) {
    return map(value, (arrayValue) => remapQuantificationIdentifiersRecursively(arrayValue, oldStainToNewStain));
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  return reduce(
    toPairs(value),
    (accumulator, [key, nestedValue]) => {
      const remappedKey = remapQuantificationIdentifier(key, oldStainToNewStain);
      accumulator[remappedKey] = remapQuantificationIdentifiersRecursively(nestedValue, oldStainToNewStain);
      return accumulator;
    },
    {} as Record<string, unknown>
  );
};

export class PresetService {
  static deleteStains(presetData: JobPreset, stainsToDelete: string[]): JobPreset {
    const presetDataWithoutStainsToDelete = cloneDeep(presetData);

    const actionKeys = ['postprocessing', 'features'];

    forEach(actionKeys, (key) => {
      const actionIndexToDelete: number[] = [];
      forEach(presetData.presetJson[key], (action: any, index: number) => {
        if (includes(stainsToDelete, action?.stain)) {
          actionIndexToDelete.push(index);
        } else if (action?.stains && isEmpty(difference(action?.stains, stainsToDelete))) {
          actionIndexToDelete.push(index);
        } else if (action?.stains && some(action?.stains, (stain) => includes(stainsToDelete, stain))) {
          pull(presetDataWithoutStainsToDelete.presetJson[key][index].stains, ...stainsToDelete);
        }
      });

      pullAt(presetDataWithoutStainsToDelete.presetJson[key], actionIndexToDelete);
    });

    return presetDataWithoutStainsToDelete;
  }

  static replaceStains(presetData: JobPreset, oldStainToNewStain: StainsActions): JobPreset {
    const presetDataWithNewStains = cloneDeep(presetData);

    const actionKeys = ['postprocessing', 'features'];
    const stainsToReplace = keys(oldStainToNewStain);
    const stainReassignmentsByStain = reduce(
      toPairs(oldStainToNewStain),
      (accumulator, [oldStain, stainAction]) => {
        if (stainAction?.reassignTo) {
          accumulator[oldStain] = stainAction.reassignTo;
        }

        return accumulator;
      },
      {} as Record<string, string>
    );

    forEach(actionKeys, (key) => {
      forEach(presetData.presetJson[key], (action: any, index: number) => {
        if (includes(stainsToReplace, action?.stain)) {
          const reassignedStain = oldStainToNewStain[action.stain]?.reassignTo;
          presetDataWithNewStains.presetJson[key][index].stain = reassignedStain || action.stain;
        } else if (some(action?.stains, (stain) => includes(stainsToReplace, stain))) {
          presetDataWithNewStains.presetJson[key][index].stains = uniq(
            map(action.stains, (stain) => {
              if (oldStainToNewStain[stain]?.reassignTo) {
                return oldStainToNewStain[stain].reassignTo;
              }
              return stain;
            })
          );
        }
      });
    });

    if (!isEmpty(stainReassignmentsByStain)) {
      forEach(actionKeys, (actionKey) => {
        presetDataWithNewStains.presetJson[actionKey] = remapQuantificationIdentifiersRecursively(
          presetDataWithNewStains.presetJson[actionKey],
          stainReassignmentsByStain
        );
      });
    }

    return presetDataWithNewStains;
  }

  static applyPreset(
    stainsActions: StainsActions,
    preset: JobPreset,
    setCalculateFeatureParams: (params: PresetApplyParams) => void,
    setPipRunName: (name: string) => void,
    setPresetStainModalOpen: (open: boolean) => void
  ): void {
    let newPreset = cloneDeep(preset);

    const stainsToDelete = keys(stainsActions).filter((stainId) => stainsActions[stainId].action === Action.DELETE);
    if (!isEmpty(stainsToDelete)) {
      newPreset = this.deleteStains(preset, stainsToDelete);
    }

    const stainsToReplace = fromPairs(
      filter(toPairs(stainsActions), ([_, stainAction]) => stainAction.action === Action.REASSIGN)
    );
    if (!isEmpty(stainsToReplace)) {
      newPreset = this.replaceStains(newPreset, stainsToReplace);
    }

    setCalculateFeatureParams(newPreset.presetJson as PresetApplyParams);
    if (newPreset.presetJson?.name) {
      setPipRunName(newPreset.presetJson.name);
    }

    setPresetStainModalOpen(false);
  }

  static selectPreset(
    preset: JobPreset,
    currentStains: string[],
    setCurrentPreset: (preset: JobPreset) => void,
    setPresetStainModalOpen: (open: boolean) => void,
    applyPresetCallback: (stainsActions: StainsActions, preset?: JobPreset) => void
  ): void {
    setCurrentPreset(preset);
    const stainsInPresetNotInCurrent = preset.stains.filter((stain) => !includes(currentStains, stain));

    if (!isEmpty(stainsInPresetNotInCurrent)) {
      setPresetStainModalOpen(true);
    } else {
      applyPresetCallback({}, preset);
    }
  }
}
